import React from "react";
import {
  Box,
  Container,
  Divider,
  Flex,
  Heading,
  HStack,
  Image,
  SimpleGrid,
  Tag,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { MEMBER_SECTIONS } from "./NextconMembersData";

/**
 * Members page UI upgrade
 * - Fixes image overflow on zoom using maxW/maxH constraints.
 * - Centralizes "director" section specifically using Flexbox.
 */

function AvatarCircle({ src, alt, sizePx = 190 }) {
  const ring = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.22)");
  const bg = useColorModeValue("rgba(255,255,255,0.88)", "rgba(255,255,255,0.04)");
  const shadow = useColorModeValue("0 14px 34px rgba(0,0,0,0.10)", "none");

  return (
    <Box
      // Use width/height with max-width to prevent overflow on zoom
      w={`${sizePx}px`}
      h={`${sizePx}px`}
      maxW="100%" 
      borderRadius="full"
      overflow="hidden"
      border="1px solid"
      borderColor={ring}
      bg={bg}
      boxShadow={shadow}
      mx="auto"
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={2.5}
    >
      <Image
        src={src}
        alt={alt}
        w="100%"
        h="100%"
        objectFit="contain"
        objectPosition="center"
        draggable={false}
      />
    </Box>
  );
}

function StackHeader({ title }) {
  const titleColor = useColorModeValue("#0F172A", "white");
  const subColor = useColorModeValue("gray.600", "gray.300");
  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");

  return (
    <Box textAlign="center">
      <Heading
        fontSize={{ base: "2xl", md: "3xl" }}
        color={titleColor}
        letterSpacing="tight"
        fontWeight="900"
      >
        {title}
      </Heading>

      <Text mt={2} color={subColor} fontWeight="600">
        {/* intentionally empty */}
      </Text>

      <Box
        mx="auto"
        mt={4}
        w={{ base: "90px", md: "120px" }}
        h="3px"
        borderRadius="999px"
        bg={useColorModeValue("rgba(34,197,94,0.35)", "rgba(34,197,94,0.45)")}
        border="1px solid"
        borderColor={border}
      />
    </Box>
  );
}

function PersonCard({ person, isMobile }) {
  const cardBg = useColorModeValue("white", "rgba(255,255,255,0.03)");
  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const nameColor = useColorModeValue("#0F172A", "white");
  const roleColor = useColorModeValue("gray.700", "gray.300");

  const sizePx = isMobile ? 170 : 190;

  return (
    <Box
      w="100%"
      maxW={{ base: "520px", md: "320px" }}
      bg={cardBg}
      border="1px solid"
      borderColor={border}
      borderRadius="26px"
      p={{ base: 6, md: 7 }}
      textAlign="center"
      boxShadow={useColorModeValue("0 14px 36px rgba(0,0,0,0.08)", "none")}
      transition="transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease"
      // Prevent overflow on zoom
      overflow="hidden" 
      _hover={{
        transform: "translateY(-2px)",
        boxShadow: useColorModeValue("0 18px 50px rgba(0,0,0,0.12)", "none"),
        borderColor: useColorModeValue("rgba(34,197,94,0.55)", "rgba(134,239,172,0.35)"),
      }}
    >
      <AvatarCircle src={person.image} alt={person.alt ?? person.name} sizePx={sizePx} />

      <Text mt={5} fontWeight="900" color={nameColor} fontSize={{ base: "lg", md: "lg" }} noOfLines={2}>
        {person.name}
      </Text>

      {person.role ? (
        <Text mt={2} fontSize={{ base: "sm", md: "md" }} color={roleColor} fontWeight="600" lineHeight={1.6} noOfLines={3}>
          {person.role}
        </Text>
      ) : null}
    </Box>
  );
}

function MembersHeroHeader() {
  const location = useLocation();
  
  const accent = "#16A34A";
  const accent2 = "#22C55E";

  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const titleColor = useColorModeValue("#0F172A", "white");
  const subColor = useColorModeValue("gray.700", "gray.300");

  const heroBg = useColorModeValue(
    "radial-gradient(1000px 420px at 12% 0%, rgba(34,197,94,0.18), transparent 60%), radial-gradient(900px 380px at 88% 25%, rgba(22,163,74,0.14), transparent 60%), linear-gradient(180deg, rgba(2,6,23,0.02), rgba(2,6,23,0.00))",
    "radial-gradient(1000px 420px at 12% 0%, rgba(34,197,94,0.20), transparent 60%), radial-gradient(900px 380px at 88% 25%, rgba(22,163,74,0.14), transparent 60%), linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.00))"
  );

  return (
    <Box pt={{ base: 6, md: 8 }} pb={{ base: 8, md: 10 }} bg={heroBg} borderBottom="1px solid" borderColor={border}>
      <Container maxW="1200px">
        <HStack spacing={2} flexWrap="wrap">
          <Tag
            size="lg"
            borderRadius="full"
            bg={useColorModeValue("white", "rgba(255,255,255,0.06)")}
            border="1px solid"
            borderColor={border}
            fontWeight="800"
          >
            University of Ottawa
          </Tag>

          <Tag
            size="lg"
            borderRadius="full"
            bg={useColorModeValue("rgba(34,197,94,0.14)", "rgba(34,197,94,0.18)")}
            color={useColorModeValue("#166534", "#86EFAC")}
            fontWeight="900"
          >
            NEXTCON Lab • SCVIC
          </Tag>
        </HStack>

        <Heading mt={4} fontSize={{ base: "3xl", md: "4xl" }} color={titleColor} letterSpacing="tight">
          <Box as="span" bgClip="text" bgGradient={`linear(to-r, ${accent}, ${accent2})`}>
            Members
          </Box>
        </Heading>

        <Text mt={3} color={subColor} fontSize={{ base: "md", md: "lg" }} maxW="85ch" lineHeight={1.8}>
          Faculty, researchers, and trainees contributing to next-generation communications, edge intelligence,
          connected mobility, and cybersecurity.
        </Text>
      </Container>
    </Box>
  );
}

export default function Members() {
  const isMobile = useSelector((state) => state.windowSize.isMobile);

  const sectionBg = useColorModeValue("white", "#0B1220");
  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");

  const accent = "#16A34A";
  const accent2 = "#22C55E";

  return (
    <Box bg={sectionBg} pb={{ base: 10, md: 14 }}>
      <MembersHeroHeader />

      {MEMBER_SECTIONS.map((section) => {
        // Check if this section is the Director section
        const isDirector = section.id === "director";

        return (
          <Box key={section.id} bg={sectionBg}>
            <Container maxW="1200px" py={{ base: 4, md: 6 }}>
              <StackHeader title={section.title} />

              {/* 
                  LOGIC: 
                  If it's the Director -> Use Flexbox to force Center alignment.
                  If it's others -> Use Grid to handle multiple items.
              */}
              {isDirector ? (
                <Flex justify="center" mt={{ base: 7, md: 9 }}>
                   {section.members.map((m, idx) => (
                    <PersonCard key={`${section.id}-${idx}`} person={m} isMobile={isMobile} />
                  ))}
                </Flex>
              ) : (
                <SimpleGrid
                  minChildWidth={section.columns ? undefined : "260px"}
                  columns={section.columns ? { base: 2, md: section.columns } : undefined}
                  spacing={{ base: 6, md: 8 }}
                  mt={{ base: 7, md: 9 }}
                  justifyItems="center"
                >
                  {section.members.map((m, idx) => (
                    <PersonCard key={`${section.id}-${idx}`} person={m} isMobile={isMobile} />
                  ))}
                </SimpleGrid>
              )}

              <Box mt={{ base: 5, md: 7 }}>
                <Divider borderColor={border} />
                <Box
                  h="3px"
                  mt={1}
                  bg={`linear-gradient(90deg, ${accent} 0%, ${accent2} 45%, rgba(34,197,94,0) 100%)`}
                />
              </Box>
            </Container>
          </Box>
        );
      })}
    </Box>
  );
}